// Pure SVG path builders for VectorShape. Everything works in the box's pixel
// space (the renderer measures the box), so a corner radius or a stroke width
// is the same number of pixels at any box size.

const f = (n) => Math.round(n * 100) / 100;

const clampRadius = (r, w, h) => Math.max(0, Math.min(r || 0, w / 2, h / 2));

// Rounded rect with one radius per corner: [topLeft, topRight, bottomRight, bottomLeft].
export function roundedRectCornersPath(x, y, w, h, radii) {
  const [tl, tr, br, bl] = radii.map((r) => clampRadius(r, w, h));
  const arc = (r, ex, ey) => (r ? `A${f(r)} ${f(r)} 0 0 1 ${f(ex)} ${f(ey)}` : "");
  return [
    `M${f(x + tl)} ${f(y)}`,
    `H${f(x + w - tr)}`,
    arc(tr, x + w, y + tr),
    `V${f(y + h - br)}`,
    arc(br, x + w - br, y + h),
    `H${f(x + bl)}`,
    arc(bl, x, y + h - bl),
    `V${f(y + tl)}`,
    arc(tl, x + tl, y),
    "Z",
  ].join("");
}

export function roundedRectPath(x, y, w, h, r) {
  if (!clampRadius(r, w, h)) return `M${f(x)} ${f(y)}H${f(x + w)}V${f(y + h)}H${f(x)}Z`;
  return roundedRectCornersPath(x, y, w, h, [r, r, r, r]);
}

export function ellipsePath(x, y, w, h) {
  const rx = w / 2;
  const ry = h / 2;
  const cy = y + ry;
  return (
    `M${f(x)} ${f(cy)}` +
    `A${f(rx)} ${f(ry)} 0 1 1 ${f(x + w)} ${f(cy)}` +
    `A${f(rx)} ${f(ry)} 0 1 1 ${f(x)} ${f(cy)}Z`
  );
}

// Polygon whose every corner is rounded with a quadratic curve. The cut is
// capped at half of each adjacent edge so neighbouring corners never overlap.
export function roundedPolygonPath(points, r) {
  if (!points.length) return "";
  if (!r || r <= 0) {
    return `M${points.map(([x, y]) => `${f(x)} ${f(y)}`).join("L")}Z`;
  }
  const n = points.length;
  const dist = (a, b) => Math.hypot(b[0] - a[0], b[1] - a[1]);
  const toward = (from, to, len) => {
    const d = dist(from, to) || 1;
    return [from[0] + ((to[0] - from[0]) * len) / d, from[1] + ((to[1] - from[1]) * len) / d];
  };
  let d = "";
  for (let i = 0; i < n; i++) {
    const v = points[i];
    const prev = points[(i - 1 + n) % n];
    const next = points[(i + 1) % n];
    const cut = Math.min(r, dist(v, prev) / 2, dist(v, next) / 2);
    const a = toward(v, prev, cut);
    const b = toward(v, next, cut);
    d += `${i === 0 ? "M" : "L"}${f(a[0])} ${f(a[1])}Q${f(v[0])} ${f(v[1])} ${f(b[0])} ${f(b[1])}`;
  }
  return `${d}Z`;
}

// Normalized (0..1) points scaled into the inset box.
export const scalePoints = (points, x, y, w, h) =>
  points.map(([px, py]) => [x + px * w, y + py * h]);

// Regular star / burst points, normalized, first point at the top.
export function starPoints(spikes, inner, radii) {
  const pts = [];
  const total = spikes * 2;
  for (let i = 0; i < total; i++) {
    const outer = i % 2 === 0;
    const base = outer ? 0.5 : inner / 2;
    const rad = radii ? radii[i % radii.length] / 2 : base;
    const angle = -Math.PI / 2 + (i * Math.PI) / spikes;
    pts.push([0.5 + rad * Math.cos(angle), 0.5 + rad * Math.sin(angle)]);
  }
  return pts;
}

export function regularPolygonPoints(sides) {
  const pts = [];
  for (let i = 0; i < sides; i++) {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / sides;
    pts.push([0.5 + 0.5 * Math.cos(angle), 0.5 + 0.5 * Math.sin(angle)]);
  }
  // Stretch to fill the unit box (a pentagon's bottom sits above y = 1).
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  const [minX, maxX, minY, maxY] = [
    Math.min(...xs),
    Math.max(...xs),
    Math.min(...ys),
    Math.max(...ys),
  ];
  return pts.map(([px, py]) => [(px - minX) / (maxX - minX), (py - minY) / (maxY - minY)]);
}

// ---- cards / callouts (x, y, w, h = inset box; r = corner radius) ----

export function cutCornerPath(x, y, w, h, r) {
  const c = Math.max(0, Math.min(r, w / 2, h / 2));
  return `M${f(x + c)} ${f(y)}H${f(x + w)}V${f(y + h)}H${f(x)}V${f(y + c)}Z`;
}

export function ticketPath(x, y, w, h, r) {
  const n = Math.max(4, Math.min(h * 0.16, w * 0.1, 22));
  const rr = Math.max(0, Math.min(r, (h / 2 - n) * 0.9, w / 4));
  const cy = y + h / 2;
  const arc = (ex, ey) => (rr ? `A${f(rr)} ${f(rr)} 0 0 1 ${f(ex)} ${f(ey)}` : "");
  return [
    `M${f(x + rr)} ${f(y)}`,
    `H${f(x + w - rr)}`,
    arc(x + w, y + rr),
    `V${f(cy - n)}`,
    `A${f(n)} ${f(n)} 0 0 0 ${f(x + w)} ${f(cy + n)}`,
    `V${f(y + h - rr)}`,
    arc(x + w - rr, y + h),
    `H${f(x + rr)}`,
    arc(x, y + h - rr),
    `V${f(cy + n)}`,
    `A${f(n)} ${f(n)} 0 0 0 ${f(x)} ${f(cy - n)}`,
    `V${f(y + rr)}`,
    arc(x + rr, y),
    "Z",
  ].join("");
}

// Speech bubble, tail at the bottom-left.
export function speechBubblePath(x, y, w, h, r) {
  const bh = h * 0.8;
  const rr = clampRadius(r, w, bh);
  const arc = (ex, ey) => (rr ? `A${f(rr)} ${f(rr)} 0 0 1 ${f(ex)} ${f(ey)}` : "");
  const t1 = Math.max(x + rr, x + w * 0.2);
  const t2 = Math.min(x + w - rr, x + w * 0.36);
  return [
    `M${f(x + rr)} ${f(y)}`,
    `H${f(x + w - rr)}`,
    arc(x + w, y + rr),
    `V${f(y + bh - rr)}`,
    arc(x + w - rr, y + bh),
    `H${f(t2)}`,
    `L${f(x + w * 0.12)} ${f(y + h)}`,
    `L${f(t1)} ${f(y + bh)}`,
    `H${f(x + rr)}`,
    arc(x, y + bh - rr),
    `V${f(y + rr)}`,
    arc(x + rr, y),
    "Z",
  ].join("");
}

// Speech bubble, tail on the left side.
export function speechBubbleLeftPath(x, y, w, h, r) {
  const tw = w * 0.12;
  const bx = x + tw;
  const bw = w - tw;
  const rr = clampRadius(r, bw, h);
  const arc = (ex, ey) => (rr ? `A${f(rr)} ${f(rr)} 0 0 1 ${f(ex)} ${f(ey)}` : "");
  const tTop = Math.max(y + rr, y + h * 0.45);
  const tBottom = Math.min(y + h - rr, y + h * 0.68);
  return [
    `M${f(bx + rr)} ${f(y)}`,
    `H${f(bx + bw - rr)}`,
    arc(bx + bw, y + rr),
    `V${f(y + h - rr)}`,
    arc(bx + bw - rr, y + h),
    `H${f(bx + rr)}`,
    arc(bx, y + h - rr),
    `V${f(tBottom)}`,
    `L${f(x)} ${f(y + h * 0.85)}`,
    `L${f(bx)} ${f(tTop)}`,
    `V${f(y + rr)}`,
    arc(bx + rr, y),
    "Z",
  ].join("");
}

export function documentPath(x, y, w, h, r) {
  const fold = Math.min(w, h) * 0.24;
  const rr = clampRadius(r, w, h);
  const arc = (ex, ey) => (rr ? `A${f(rr)} ${f(rr)} 0 0 1 ${f(ex)} ${f(ey)}` : "");
  return [
    `M${f(x + rr)} ${f(y)}`,
    `H${f(x + w - fold)}`,
    `L${f(x + w)} ${f(y + fold)}`,
    `V${f(y + h - rr)}`,
    arc(x + w - rr, y + h),
    `H${f(x + rr)}`,
    arc(x, y + h - rr),
    `V${f(y + rr)}`,
    arc(x + rr, y),
    "Z",
    // The fold itself, drawn as a second sub-path.
    `M${f(x + w - fold)} ${f(y)}V${f(y + fold)}H${f(x + w)}Z`,
  ].join("");
}

export function halfDiscPath(x, y, w, h) {
  return `M${f(x)} ${f(y + h)}A${f(w / 2)} ${f(h)} 0 0 1 ${f(x + w)} ${f(y + h)}Z`;
}

export function quarterDiscPath(x, y, w, h) {
  return `M${f(x)} ${f(y + h)}V${f(y)}A${f(w)} ${f(h)} 0 0 1 ${f(x + w)} ${f(y + h)}Z`;
}
